import React, { ReactElement, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { colorsPalette } from '../../../../../shared/ui/styles/colors-palette';
import { PHONE_CODE_LENGTH } from '../../../../infrastructure/types/features/auth/auth-types';

interface PhoneCodeProps {
  onCodeChange: (code: string) => void;
}

const PhoneCode = function ({ onCodeChange }: PhoneCodeProps): ReactElement {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [digits, setDigits] = useState<string[]>(Array(PHONE_CODE_LENGTH).fill(''));

  const updateCode = useCallback(
    (newDigits: string[]) => {
      setDigits(newDigits);
      const code = newDigits.join('');
      onCodeChange(code);
    },
    [onCodeChange],
  );

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (value && !/^\d$/.test(value)) return;
      const newDigits = [...digits];
      newDigits[index] = value;
      updateCode(newDigits);
      if (value) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [digits, updateCode],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index]) {
        inputRefs.current[index - 1]?.focus();
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        updateCode(newDigits);
      }
    },
    [digits, updateCode],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PHONE_CODE_LENGTH);
      const newDigits = [...digits];
      pasted.split('').forEach((char, i) => {
        newDigits[i] = char;
      });
      updateCode(newDigits);
      inputRefs.current[Math.min(pasted.length, PHONE_CODE_LENGTH - 1)]?.focus();
    },
    [digits, updateCode],
  );

  return (
    <Wrapper>
      {Array.from({ length: PHONE_CODE_LENGTH }, (_, i) => (
        <CodeInput
          key={i}
          ref={el => {
            inputRefs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i]}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          autoComplete="one-time-code"
        />
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;
const CodeInput = styled.input`
  width: 48px;
  height: 58px;
  padding: 0;
  border: 1px solid ${colorsPalette.lightGray400};
  border-radius: 6px;
  background: ${colorsPalette.white};
  font-family: 'SkorozvonRoboto';
  font-weight: 500;
  font-size: 38px;
  line-height: 46px;
  color: ${colorsPalette.gray600};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${colorsPalette.dustyBlue700};
  }
`;

export { PhoneCode };
